import { auth } from './firebase';

const PROJECT_ID = import.meta.env.VITE_PROJECT_ID || 'fir-webapp-bidlogiq';
const FILE_CONVERTER_URL = `https://${import.meta.env.VITE_GCP_REGION || 'australia-southeast1'}-${PROJECT_ID}.cloudfunctions.net/http-file-converter`;

type ConversionStatus = 'processing' | 'success' | 'failed' | 'pending' | 'missing_file';

interface ConversionResult {
  success: boolean;
  error?: string;
  filePath?: string;
  filePaths?: string[];
  [key: string]: unknown;
}

interface FileConversionRequest {
  clientId: string;
  filePath: string;
  teamId?: string | undefined;
  storageType?: string | undefined;
}

interface BatchFileConversionRequest {
  clientId: string;
  files: string[];
  teamId?: string | undefined;
  storageType?: string | undefined;
}

const FileConverterService = {
  async triggerFileConversion(
    clientId: string,
    filePath: string,
    teamId?: string,
    storageType?: string
  ): Promise<ConversionResult> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken: string = await user.getIdToken();

      const requestBody: FileConversionRequest = {
        clientId: clientId,
        filePath: filePath,
        teamId,
        storageType,
      };

      const response = await fetch(FILE_CONVERTER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return {
        success: true,
        ...result,
      };
    } catch (error: unknown) {
      const err = error as Error;
      return {
        success: false,
        error: err.message,
        filePath: filePath,
      };
    }
  },

  async triggerBatchFileConversion(
    clientId: string,
    filePaths: string[],
    teamId?: string,
    storageType?: string
  ): Promise<ConversionResult> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const idToken: string = await user.getIdToken();

      const requestBody: BatchFileConversionRequest = {
        clientId: clientId,
        files: filePaths,
        teamId,
        storageType,
      };

      const response = await fetch(FILE_CONVERTER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return {
        success: true,
        ...result,
      };
    } catch (error: unknown) {
      const err = error as Error;
      return {
        success: false,
        error: err.message,
        filePaths: filePaths,
      };
    }
  },

  getConversionStatusText(status: ConversionStatus): string {
    switch (status) {
      case 'processing':
        return '🔄 Converting...';
      case 'success':
        return '✅ Converted';
      case 'failed':
        return '❌ Failed';
      case 'pending':
        return '⏳ Pending';
      case 'missing_file':
        return '❌ Delete and reupload (File Missing)';
      default:
        return '❓ Unknown';
    }
  },

  getConversionStatusColor(status: ConversionStatus): string {
    switch (status) {
      case 'processing':
        return '#ff9800';
      case 'success':
        return '#4caf50';
      case 'failed':
      case 'missing_file':
        return '#f44336';
      case 'pending':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  },

  shouldConvertFile(fileName: string): boolean {
    const convertibleExtensions: string[] = [
      '.pdf',
      '.doc',
      '.docx',
      '.txt',
      '.rtf',
      '.xls',
      '.xlsx',
      '.csv',
      '.ppt',
      '.pptx',
      '.odt',
      '.ods',
      '.odp',
    ];

    const extension: string = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return convertibleExtensions.includes(extension);
  },

  formatFilePathForConversion(rawPath: string): string {
    const path = rawPath.replace(/^gs:\/\/[^/]+\//, '');
    return path;
  },
};

export default FileConverterService;
export type {
  BatchFileConversionRequest,
  ConversionResult,
  ConversionStatus,
  FileConversionRequest,
};
